import { useEffect, useState } from 'react'
import {
  ArrowProps,
  calculateArrowLevels,
  getArrowConfigByDistance,
  getBrokenArrowConfig,
  getMaximumArrowLevel,
  getWidthForLevel,
} from '../Arrow'
import { useScaleArrows } from './useScaleArrows'
import { appointmentDistanceFormatter } from '../../../../../../utils/appointmentDistanceFromatter'
import {
  Appointment,
  TreatmentPanelFilter,
} from '../../../types/treatments.types'
import { useTranslation } from '../../../../../../common/hooks'
import { useTreatmentPanelData } from '../TreatmentPanelProvider'

interface UseArrowProps {
  filterValue: TreatmentPanelFilter
  visibleAppointments: Appointment[]
}

interface UseArrowPropsReturn {
  arrowProps: ArrowProps[]
  widthOfArrows: number
}
export const useArrowProps = ({
  filterValue,
  visibleAppointments,
}: UseArrowProps): UseArrowPropsReturn => {
  const { treatment, hoveredTreatmentItem } = useTreatmentPanelData()
  const [t] = useTranslation()
  const [arrowProps, setArrowProps] = useState<ArrowProps[]>([])

  const { scaleArrows } = useScaleArrows()
  const widthOfArrows = getWidthForLevel(
    getMaximumArrowLevel(arrowProps),
    scaleArrows
  )

  const createArrowProps = (): ArrowProps[] => {
    const newArrowProps = (
      !filterValue.searchTerm?.length ? visibleAppointments : []
    ).reduce<ArrowProps[]>((acc, appointment) => {
      const arrows = appointment.dependent
        .filter((dependent) =>
          visibleAppointments.find(
            (appointment) => dependent.toId === appointment.id
          )
        )
        .map((dependent) => ({
          start: `arrow-row-${appointment.id}`,
          end: `arrow-row-${dependent.toId}`,
          labelText: appointmentDistanceFormatter(
            dependent.distance,
            t,
            dependent.type
          ),
          level: 1,
          highlighted:
            hoveredTreatmentItem?.id === dependent.toId ||
            hoveredTreatmentItem?.id === appointment.id,
          ...(dependent.isBroken
            ? getBrokenArrowConfig()
            : getArrowConfigByDistance(dependent.distance)),
          maxDistance: undefined,
        }))
      return [...acc, ...arrows]
    }, [])
    calculateArrowLevels(
      newArrowProps,
      visibleAppointments.map((appointment) => `arrow-row-${appointment.id}`)
    )

    return newArrowProps
  }

  useEffect(() => {
    setArrowProps(createArrowProps())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredTreatmentItem?.id, filterValue.searchTerm?.length])

  useEffect(() => {
    setArrowProps([])
    setTimeout(() => setArrowProps(createArrowProps()), 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatment?.appointments])

  return {
    arrowProps,
    widthOfArrows,
  }
}
